import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Dialog } from '@headlessui/react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { 
  XMarkIcon, 
  PhotoIcon, 
  PlusIcon, 
  TrashIcon,
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
  TagIcon,
  CurrencyDollarIcon,
  SwatchIcon,
  ScaleIcon,
  SparklesIcon,
  CameraIcon,
  MagnifyingGlassIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import ProductDesiField from './ProductDesiField';

// getCroppedImg fonksiyonu aynı kalıyor, değişiklik yok.
function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (!blob) {
                reject(new Error('Canvas blob oluşturulamadı.'));
                return;
            }
            const fileExtension = fileName.split('.').pop();
            const newFileName = `${fileName.substring(0, fileName.lastIndexOf('.')) || 'cropped_image'}-${Date.now()}.${fileExtension}`;
            const croppedFile = new File([blob], newFileName, { type: 'image/jpeg' });
            resolve(croppedFile);
        }, 'image/jpeg', 1.0);
    });
}

const predefinedColors = ['Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Siyah', 'Beyaz', 'Pembe', 'Mor', 'Turuncu', 'Gri', 'Lacivert', 'Bej', 'Kahverengi'];
const predefinedSizes = ['0-3 Ay', '3-6 Ay', '6-9 Ay', '9-12 Ay', '12-18 Ay', '18-24 Ay', '2 Yaş', '3 Yaş', '4 Yaş', '5 Yaş', '6 Yaş', '7 Yaş', '8 Yaş', '9 Yaş', '10 Yaş', 'S', 'M', 'L', 'XL'];
const predefinedCategories = ['T-shirt', 'Gömlek', 'Pantolon', 'Etek', 'Elbise', 'Şort', 'Mont', 'Ayakkabı', 'Aksesuar'];
const predefinedGenders = ['Kız Çocuk', 'Erkek Çocuk', 'Unisex'];


function ProductForm({ product, closeModal, fetchProducts }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    // --- YENİ EKLENDİ: `details` state'i eklendi ---
    const [details, setDetails] = useState('');
    const [price, setPrice] = useState('');
    const [category, setCategory] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [gender, setGender] = useState('');
    const [minStock, setMinStock] = useState(2);
    const [maxStock, setMaxStock] = useState(10);
    const [priceWarningDays, setPriceWarningDays] = useState(30);
    const [desi, setDesi] = useState(1.0);
    const [variants, setVariants] = useState([]);
    const [mainImageFile, setMainImageFile] = useState(null);
    const [mainImageSrc, setMainImageSrc] = useState('');
    const mainImgRef = useRef(null);
    const [mainCrop, setMainCrop] = useState();
    const [mainCompletedCrop, setMainCompletedCrop] = useState(null);
    const [existingMainImageUrl, setExistingMainImageUrl] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [existingGalleryImageUrls, setExistingGalleryImageUrls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedColors, setSelectedColors] = useState([]);
    const [selectedSizes, setSelectedSizes] = useState([]);
    
    // Varyant fotoğraf kırpma için state'ler
    const [variantCrops, setVariantCrops] = useState({});
    const [variantCompletedCrops, setVariantCompletedCrops] = useState({});
    const [variantImgRefs, setVariantImgRefs] = useState({});
    const [editingVariantIndex, setEditingVariantIndex] = useState(null);
    
    const { token } = useAuth();

    // Hiyerarşik kategori listesini düz listeye çevir
    const flattenCategories = (categories, level = 0) => {
        let flattened = [];
        categories.forEach(category => {
            flattened.push({
                ...category,
                level,
                displayName: '  '.repeat(level) + category.name
            });
            if (category.children && category.children.length > 0) {
                flattened = flattened.concat(flattenCategories(category.children, level + 1));
            }
        });
        return flattened;
    };

    // Kategorileri yükle
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/api/categories');
                const hierarchicalCategories = response.data || [];
                const flatCategories = flattenCategories(hierarchicalCategories);
                setAvailableCategories(flatCategories);
            } catch (error) {
                console.error('Kategoriler yüklenirken hata:', error);
                toast.error('Kategoriler yüklenemedi');
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (product) {
            setName(product.name || '');
            setDescription(product.description || '');
            // --- YENİ EKLENDİ: `details` state'i ürün düzenlenirken dolduruluyor ---
            setDetails(product.details || '');
            setPrice(product.price || '');
            setCategory(product.category || '');
            setGender(product.gender || '');
            setMinStock(product.min_stock || 2);
            setMaxStock(product.max_stock || 10);
            setPriceWarningDays(product.price_warning_days || 30);
            setDesi(product.desi || 1.0);
            setVariants(product.variants || []);
            const initialColors = [...new Set((product.variants || []).map(v => v.color))].filter(Boolean);
            const initialSizes = [...new Set((product.variants || []).map(v => v.size))].filter(Boolean);
            setSelectedColors(initialColors);
            setSelectedSizes(initialSizes);
            setExistingMainImageUrl(product.main_image_url || null);
            setExistingGalleryImageUrls(product.gallery_images || []);
            
            // Ürünün kategorilerini yükle (eğer varsa)
            console.log('Tüm ürün verisi:', product);
            console.log('Ürün kategorileri:', product.categories);
            console.log('Kategori array uzunluğu:', product.categories?.length);
            
            if (product.categories && product.categories.length > 0) {
                console.log('Her kategori detayı:');
                product.categories.forEach((cat, index) => {
                    console.log(`Kategori ${index}:`, cat);
                });
                
                const categoryIds = product.categories.map(cat => cat.id).filter(Boolean);
                console.log('Filtrelenmiş kategori ID\'leri:', categoryIds);
                setSelectedCategories(categoryIds);
            } else {
                console.log('Ürünün kategorisi yok veya boş');
                setSelectedCategories([]);
            }
            
            // Ana görsel düzenleme modunda temizle
            setMainImageFile(null);
            setMainImageSrc('');
            setMainCrop();
            setMainCompletedCrop(null);
        } else {
            setCategory(predefinedCategories[0]);
            setGender(predefinedGenders[0]);
        }
    }, [product]);
    
    // Geri kalan fonksiyonlar aynı kalabilir, sadece handleSubmit'i güncelleyeceğiz.
    // ...
    const validateFileSize = (file, maxSizeMB = 25) => {
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            toast.error(`Dosya boyutu çok büyük! Maksimum ${maxSizeMB}MB yükleyebilirsiniz. Seçilen dosya: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
            return false;
        }
        return true;
    };

    // Fotoğraf sıkıştırma fonksiyonu
    const compressImage = (file, maxWidth = 2400, quality = 0.95) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                // Orantılı boyutlandırma
                let { width, height } = img;
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
                
                canvas.width = width;
                canvas.height = height;
                
                // Fotoğrafı çiz ve sıkıştır
                ctx.drawImage(img, 0, 0, width, height);
                
                canvas.toBlob((blob) => {
                    const compressedFile = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    resolve(compressedFile);
                }, 'image/jpeg', quality);
            };
            
            img.src = URL.createObjectURL(file);
        });
    };

    const handleMainImageChange = async (event) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            
            // Dosya boyutu kontrolü
            if (!validateFileSize(file, 25)) { // 25MB ham dosya limiti
                event.target.value = ''; // Input'u temizle
                return;
            }
            
            try {
                // Büyük dosyaları sıkıştır
                let processedFile = file;
                if (file.size > 10 * 1024 * 1024) { // 10MB'dan büyükse sıkıştır
                    toast.info('Büyük fotoğraf tespit edildi, kalitesi korunarak boyutu düşürülüyor...');
                    processedFile = await compressImage(file, 2400, 0.95);
                    toast.success(`Fotoğraf sıkıştırıldı: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
                }
                
                setMainImageFile(processedFile);
                const reader = new FileReader();
                reader.addEventListener('load', () => setMainImageSrc(reader.result?.toString() || ''));
                reader.readAsDataURL(processedFile);
                setExistingMainImageUrl(null);
                
                toast.success(`Ana görsel hazır: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
            } catch (error) {
                console.error('Fotoğraf işleme hatası:', error);
                toast.error('Fotoğraf işlenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
                event.target.value = '';
            }
        }
    };

    const handleGalleryFilesChange = (event) => {
        const files = Array.from(event.target.files);
        const validFiles = [];
        let totalSize = 0;
        
        for (const file of files) {
            if (validateFileSize(file)) {
                validFiles.push(file);
                totalSize += file.size;
            }
        }
        
        if (validFiles.length > 0) {
            setGalleryFiles(prev => [...prev, ...validFiles]);
            toast.success(`${validFiles.length} galeri fotoğrafı eklendi. Toplam boyut: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
        }
        
        // Input'u temizle
        event.target.value = '';
    };

    const onMainImageLoad = (e) => {
        mainImgRef.current = e.currentTarget;
        const { width, height } = e.currentTarget;
        setMainCrop(centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height));
    };

    const removeExistingGalleryImage = (url) => setExistingGalleryImageUrls(p => p.filter(x => x !== url));
    const removeNewGalleryFile = (file) => setGalleryFiles(p => p.filter(x => x !== file));
    const handleVariantChange = (index, field, value) => {
        const newVariants = [...variants];
        newVariants[index][field] = value;
        setVariants(newVariants);
    };

    const handleVariantImageChange = (index, event) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            
            // Dosya boyutu kontrolü
            if (!validateFileSize(file)) {
                event.target.value = ''; // Input'u temizle
                return;
            }
            
            const newVariants = [...variants];
            newVariants[index].variant_image_file = file;
            newVariants[index].variant_image_preview = URL.createObjectURL(file);
            newVariants[index].variant_image_url = null;
            setVariants(newVariants);
            
            // Kırpma için düzenleme modunu aç
            setEditingVariantIndex(index);
            
            toast.success(`Varyant fotoğrafı yüklendi: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        }
    };

    const handleRemoveVariantImage = (index) => {
        const newVariants = [...variants];
        newVariants[index].variant_image_file = null;
        newVariants[index].variant_image_preview = null;
        newVariants[index].variant_image_url = null;
        setVariants(newVariants);
        
        // Kırpma verilerini temizle
        const newCrops = { ...variantCrops };
        const newCompletedCrops = { ...variantCompletedCrops };
        delete newCrops[index];
        delete newCompletedCrops[index];
        setVariantCrops(newCrops);
        setVariantCompletedCrops(newCompletedCrops);
        
        if (editingVariantIndex === index) {
            setEditingVariantIndex(null);
        }
    };

    const onVariantImageLoad = (index, e) => {
        const imgRef = e.currentTarget;
        setVariantImgRefs(prev => ({ ...prev, [index]: imgRef }));
        
        const { width, height } = imgRef;
        const crop = centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height);
        setVariantCrops(prev => ({ ...prev, [index]: crop }));
    };

    const handleVariantCropComplete = async (index) => {
        const imgRef = variantImgRefs[index];
        const completedCrop = variantCompletedCrops[index];
        const variant = variants[index];
        
        if (imgRef && completedCrop && variant.variant_image_file) {
            try {
                const croppedFile = await getCroppedImg(imgRef, completedCrop, variant.variant_image_file.name);
                const newVariants = [...variants];
                newVariants[index].variant_image_file = croppedFile;
                newVariants[index].variant_image_preview = URL.createObjectURL(croppedFile);
                setVariants(newVariants);
                setEditingVariantIndex(null);
                toast.success('Varyant fotoğrafı başarıyla kırpıldı!');
            } catch (error) {
                console.error('Varyant fotoğraf kırpma hatası:', error);
                toast.error('Fotoğraf kırpılırken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
            }
        }
    };

    const handleColorSelection = (color) => setSelectedColors(p => p.includes(color) ? p.filter(c => c !== color) : [...p, color]);
    const handleSizeSelection = (size) => setSelectedSizes(p => p.includes(size) ? p.filter(s => s !== size) : [...p, size]);

    useEffect(() => {
        const newVariants = [];
        if (selectedColors.length > 0 && selectedSizes.length > 0) {
            selectedColors.forEach(color => {
                selectedSizes.forEach(size => {
                    const existingVariant = product?.variants?.find(v => v.color === color && v.size === size) || variants.find(v => v.color === color && v.size === size);
                    newVariants.push({
                        color: color,
                        size: size,
                        stock: existingVariant ? existingVariant.stock : 0,
                        price_override: existingVariant ? existingVariant.price_override : null,
                        variant_image_url: existingVariant ? existingVariant.variant_image_url : null,
                        variant_image_file: existingVariant ? existingVariant.variant_image_file : null,
                        variant_image_preview: existingVariant ? existingVariant.variant_image_preview : null
                    });
                });
            });
            setVariants(newVariants);
        } else {
            setVariants([]);
        }
    }, [selectedColors, selectedSizes]);
    

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isNaN(parseFloat(price)) || !price) {
            toast.error("Lütfen geçerli bir Genel Fiyat giriniz.");
            return;
        }

        setLoading(true);

        if (!token) {
            toast.error("Yetkilendirme hatası: Lütfen tekrar giriş yapın.");
            setLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        // --- YENİ EKLENDİ: `details` verisi forma ekleniyor ---
        formData.append('details', details);
        formData.append('price', parseFloat(price));
        formData.append('category', category);
        formData.append('categories', JSON.stringify(selectedCategories));
        formData.append('gender', gender);
        formData.append('min_stock', minStock);
        formData.append('max_stock', maxStock);
        formData.append('price_warning_days', priceWarningDays);
        formData.append('desi', desi);

        const variantsToSubmit = variants.map((variant, index) => {
            const { variant_image_file, variant_image_preview, ...rest } = variant;
            
            if (variant_image_file) {
                const variantFileKey = `variantImage_${index}`;
                formData.append(variantFileKey, variant_image_file);
            }
            return rest;
        });
        formData.append('variants', JSON.stringify(variantsToSubmit));

        if (mainImageSrc && mainCompletedCrop && mainImgRef.current) {
            try {
                const croppedFile = await getCroppedImg(mainImgRef.current, mainCompletedCrop, mainImageFile.name);
                formData.append('mainImage', croppedFile);
            } catch (cropError) {
                toast.error(`Ana görsel kırpılırken hata oluştu: ${cropError.message}`);
                setLoading(false);
                return;
            }
        } else if (existingMainImageUrl && !mainImageFile) {
            formData.append('main_image_url_existing', existingMainImageUrl);
        }

        formData.append('gallery_image_urls_existing', JSON.stringify(existingGalleryImageUrls));
        galleryFiles.forEach((file) => formData.append('galleryImages', file));

        const url = product ? `/api/products/${product.id}` : '/api/products';
        const method = product ? 'PUT' : 'POST';

        try {
            // FormData hazır - Boyut kontrolü
            let totalSize = 0;
            for (let [key, value] of formData.entries()) {
                if (value instanceof File) {
                    totalSize += value.size;
                }
            }
            
            if (totalSize > 100 * 1024 * 1024) { // 100MB toplam limit
                toast.error('Toplam dosya boyutu çok büyük! Maksimum 100MB yükleyebilirsiniz.');
                setLoading(false);
                return;
            }
            
            // Progress toast
            const progressToast = toast.info(
                <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Fotoğraflar yükleniyor... ({(totalSize / 1024 / 1024).toFixed(2)}MB)</span>
                </div>,
                { autoClose: false }
            );
            
            // Retry mekanizması ile API çağrısı
            let retryCount = 0;
            const maxRetries = 2;
            
            while (retryCount <= maxRetries) {
                try {
                    await api(url, {
                        method,
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: formData,
                        timeout: 300000 // 5 dakika timeout
                    });
                    break; // Başarılı olursa döngüden çık
                } catch (apiError) {
                    retryCount++;
                    
                    if (retryCount > maxRetries) {
                        throw apiError; // Son deneme de başarısızsa hatayı fırlat
                    }
                    
                    // 502 veya 503 hatalarında tekrar dene
                    if (apiError.message.includes('502') || apiError.message.includes('503') || 
                        apiError.message.includes('Incomplete response')) {
                        
                        toast.info(`Bağlantı sorunu tespit edildi. Tekrar deneniyor... (${retryCount}/${maxRetries})`);
                        
                        // 2 saniye bekle ve tekrar dene
                        await new Promise(resolve => setTimeout(resolve, 2000));
                        continue;
                    } else {
                        throw apiError; // Diğer hatalar için hemen fırlat
                    }
                }
            }

            toast.dismiss(progressToast);
            toast.success(
                <div className="space-y-1">
                    <p className="font-bold">✅ Başarılı!</p>
                    <p className="text-sm">Ürün başarıyla {product ? 'güncellendi' : 'eklendi'}!</p>
                </div>
            );
            fetchProducts();
            closeModal();

        } catch (err) {
            console.error('Ürün kaydetme hatası:', err);
            
            let errorMessage = 'Bilinmeyen bir hata oluştu.';
            
            if (err.message.includes('502')) {
                errorMessage = 'Sunucu geçici olarak kullanılamıyor. Lütfen birkaç dakika sonra tekrar deneyin.';
            } else if (err.message.includes('413')) {
                errorMessage = 'Dosyalar çok büyük. Lütfen daha küçük fotoğraflar yükleyin.';
            } else if (err.message.includes('400')) {
                errorMessage = 'Geçersiz veri. Lütfen tüm alanları kontrol edin.';
            } else if (err.message.includes('Dosya boyutu')) {
                errorMessage = 'Fotoğraf boyutu çok büyük. Maksimum 25MB yükleyebilirsiniz.';
            } else if (err.message.includes('network') || err.message.includes('fetch')) {
                errorMessage = 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            toast.error(
                <div className="space-y-2">
                    <p className="font-bold text-red-800">Ürün Kaydedilemedi!</p>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                        <p className="font-medium">Öneriler:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Fotoğraf boyutlarını kontrol edin (max 25MB)</li>
                            <li>İnternet bağlantınızı kontrol edin</li>
                            <li>Tüm zorunlu alanları doldurun</li>
                            <li>Birkaç dakika sonra tekrar deneyin</li>
                        </ul>
                    </div>
                </div>, 
                { 
                    autoClose: false,
                    className: 'toast-error-custom'
                }
            );
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="overflow-y-auto max-h-[95vh] bg-gradient-to-br from-gray-50 to-white">
            {/* Modern Header with Gradient */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-5 shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-gradient-to-r from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg">
                                {product ? (
                                    <AdjustmentsHorizontalIcon className="w-6 h-6 text-white" />
                                ) : (
                                    <SparklesIcon className="w-6 h-6 text-white" />
                                )}
                            </div>
                        </div>
                        <div>
                            <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                                {product ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
                            </Dialog.Title>
                            <p className="text-sm text-gray-600 mt-1">
                                {product ? 'Mevcut ürün bilgilerini güncelleyin' : 'Yeni ürün bilgilerini girin ve vitrininizi zenginleştirin'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="hidden sm:flex items-center space-x-2 text-xs text-gray-500">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span>Otomatik Kayıt</span>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-8 space-y-8">
                {/* Temel Bilgiler Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-green to-brand-green-light"></div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-brand-green to-brand-green-light rounded-lg flex items-center justify-center">
                                <TagIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-gray-900">Temel Bilgiler</h4>
                                <p className="text-xs text-gray-500">Ürününüzün temel özelliklerini belirleyin</p>
                            </div>
                        </div>
                        <div className="hidden sm:block">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <label htmlFor="name" className="flex items-center text-sm font-semibold text-gray-700">
                                <span className="flex items-center">
                                    Ürün Adı 
                                    <span className="text-red-500 ml-1">*</span>
                                    <span className="ml-2 text-xs text-gray-500">(Vitrinde görünecek)</span>
                                </span>
                            </label>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    id="name" 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                    required 
                                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200 text-gray-900 placeholder-gray-400"
                                    placeholder="Örn: Çocuk Pamuklu T-shirt"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <div className={`w-2 h-2 rounded-full ${name ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <label htmlFor="price" className="flex items-center text-sm font-semibold text-gray-700">
                                <CurrencyDollarIcon className="w-4 h-4 mr-1 text-brand-green" />
                                Genel Fiyat (₺) 
                                <span className="text-red-500 ml-1">*</span>
                                <span className="ml-2 text-xs text-gray-500">(Tüm varyantlar için)</span>
                            </label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    id="price" 
                                    value={price} 
                                    onChange={(e) => setPrice(e.target.value)} 
                                    step="0.01" 
                                    required 
                                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200 text-gray-900 placeholder-gray-400"
                                    placeholder="0.00"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <span className="text-gray-400 text-sm">₺</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kategori Bilgileri Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-500"></div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                                <SwatchIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-gray-900">Kategori Bilgileri</h4>
                                <p className="text-xs text-gray-500">Ürününüzün kategorisini ve hedef kitlesini belirleyin</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <label className="block text-sm font-semibold text-gray-700">
                                Kategoriler <span className="text-red-500">*</span>
                            </label>
                            <div className="max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-4 bg-white">
                                {availableCategories.length === 0 ? (
                                    <p className="text-gray-500 text-sm">Kategoriler yükleniyor...</p>
                                ) : (
                                    <div className="space-y-2">
                                        {availableCategories.map((cat) => (
                                            <label key={cat.id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCategories.includes(cat.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedCategories([...selectedCategories, cat.id]);
                                                        } else {
                                                            setSelectedCategories(selectedCategories.filter(id => id !== cat.id));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                                />
                                                <div className="flex items-center space-x-2">
                                                    {cat.icon && <i className={`${cat.icon} text-brand-green`}></i>}
                                                    <span className={`text-sm font-medium text-gray-900 ${cat.level > 0 ? 'ml-4' : ''}`}>
                                                        {cat.level > 0 && '└─ '}{cat.name}
                                                    </span>
                                                    {cat.level > 0 && <span className="text-xs text-gray-500">(Alt kategori)</span>}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {selectedCategories.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedCategories.map(catId => {
                                        const cat = availableCategories.find(c => c.id === catId);
                                        return cat ? (
                                            <span key={catId} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-green text-white">
                                                {cat.name}
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedCategories(selectedCategories.filter(id => id !== catId))}
                                                    className="ml-2 text-white hover:text-gray-200"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ) : null;
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="space-y-3">
                            <label htmlFor="gender" className="block text-sm font-semibold text-gray-700">
                                Hedef Kitle / Cinsiyet <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select 
                                    id="gender" 
                                    value={gender} 
                                    onChange={(e) => setGender(e.target.value)} 
                                    required 
                                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200 bg-white text-gray-900 appearance-none"
                                >
                                    {predefinedGenders.map(gen => <option key={gen} value={gen}>{gen}</option>)}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Açıklama Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                    <div className="flex items-center space-x-2 mb-6">
                        <TagIcon className="w-5 h-5 text-brand-green" />
                        <h4 className="text-lg font-semibold text-gray-900">Ürün Açıklamaları</h4>
                    </div>
                    
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Kısa Açıklama <span className="text-xs text-gray-500">(Ürün kartında görünür)</span>
                            </label>
                            <textarea 
                                id="description" 
                                value={description} 
                                onChange={(e) => setDescription(e.target.value)} 
                                rows="3" 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-colors resize-none"
                                placeholder="Ürünün kısa tanımını yazın..."
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <label htmlFor="details" className="block text-sm font-medium text-gray-700">
                                Detaylı Açıklama <span className="text-xs text-gray-500">(Ürün detay sayfasında görünür)</span>
                            </label>
                            <textarea 
                                id="details" 
                                value={details} 
                                onChange={(e) => setDetails(e.target.value)} 
                                rows="6" 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-colors resize-none"
                                placeholder="Örn: %100 pamuklu kumaştan üretilmiştir. 30 derecede yıkayınız. Ütü sıcaklığı orta seviyede olmalıdır..."
                            />
                        </div>
                    </div>
                </div>

                {/* Stok ve Fiyat Ayarları Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                    <div className="flex items-center space-x-2 mb-6">
                        <ScaleIcon className="w-5 h-5 text-brand-green" />
                        <h4 className="text-lg font-semibold text-gray-900">Stok ve Fiyat Ayarları</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="minStock" className="block text-sm font-medium text-gray-700">
                                Min. Stok Uyarısı
                            </label>
                            <input 
                                type="number" 
                                id="minStock" 
                                value={minStock} 
                                onChange={(e) => setMinStock(e.target.value)} 
                                required 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-colors"
                                min="0"
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="maxStock" className="block text-sm font-medium text-gray-700">
                                Maks. Stok Önerisi
                            </label>
                            <input 
                                type="number" 
                                id="maxStock" 
                                value={maxStock} 
                                onChange={(e) => setMaxStock(e.target.value)} 
                                required 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-colors"
                                min="0"
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="priceWarningDays" className="block text-sm font-medium text-gray-700">
                                Fiyat Uyarı Periyodu
                            </label>
                            <select 
                                id="priceWarningDays" 
                                value={priceWarningDays} 
                                onChange={(e) => setPriceWarningDays(e.target.value)} 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-colors bg-white"
                            >
                                <option value="30">1 Ay</option>
                                <option value="60">2 Ay</option>
                                <option value="90">3 Ay</option>
                                <option value="180">6 Ay</option>
                                <option value="365">12 Ay</option>
                            </select>
                        </div>
                        <ProductDesiField 
                            desi={desi}
                            onChange={setDesi}
                            className=""
                        />
                    </div>
                </div>

                {/* Renk Seçimi Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                    <div className="flex items-center space-x-2 mb-6">
                        <SwatchIcon className="w-5 h-5 text-brand-green" />
                        <h4 className="text-lg font-semibold text-gray-900">Renk Seçimi</h4>
                        <span className="text-sm text-gray-500">({selectedColors.length} seçili)</span>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex flex-wrap gap-3 max-h-48 overflow-y-auto">
                            {predefinedColors.map((color) => (
                                <button 
                                    key={color} 
                                    type="button" 
                                    onClick={() => handleColorSelection(color)} 
                                    className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105 ${
                                        selectedColors.includes(color) 
                                            ? 'bg-brand-green text-white shadow-lg ring-2 ring-brand-green ring-offset-2' 
                                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 shadow-sm'
                                    }`}
                                >
                                    {selectedColors.includes(color) && (
                                        <CheckCircleIcon className="w-4 h-4 absolute -top-1 -right-1 text-white bg-brand-green rounded-full" />
                                    )}
                                    {color}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Beden Seçimi Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                    <div className="flex items-center space-x-2 mb-6">
                        <ScaleIcon className="w-5 h-5 text-brand-green" />
                        <h4 className="text-lg font-semibold text-gray-900">Beden Seçimi</h4>
                        <span className="text-sm text-gray-500">({selectedSizes.length} seçili)</span>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex flex-wrap gap-3 max-h-48 overflow-y-auto">
                            {predefinedSizes.map((size) => (
                                <button 
                                    key={size} 
                                    type="button" 
                                    onClick={() => handleSizeSelection(size)} 
                                    className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105 ${
                                        selectedSizes.includes(size) 
                                            ? 'bg-brand-green text-white shadow-lg ring-2 ring-brand-green ring-offset-2' 
                                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 shadow-sm'
                                    }`}
                                >
                                    {selectedSizes.includes(size) && (
                                        <CheckCircleIcon className="w-4 h-4 absolute -top-1 -right-1 text-white bg-brand-green rounded-full" />
                                    )}
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                
                {/* Varyant Yönetimi Kartı */}
                {variants.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-teal-500"></div>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                                    <AdjustmentsHorizontalIcon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-900">Ürün Varyantları</h4>
                                    <p className="text-xs text-gray-500">Renk ve beden kombinasyonlarını yönetin</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full font-medium">
                                    {variants.length} Varyant
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {variants.map((variant, index) => (
                                <div key={`${variant.color}-${variant.size}-${index}`} className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                                    <div className="flex-1 min-w-[120px] sm:min-w-[150px]">
                                        <p className="text-sm font-semibold text-gray-700">Renk: <span className="font-normal">{variant.color}</span></p>
                                        <p className="text-sm font-semibold text-gray-700">Beden: <span className="font-normal">{variant.size}</span></p>
                                    </div>
                                    <div className="w-full sm:w-32"><label htmlFor={`stock-${index}`} className="block text-xs font-medium text-gray-600 mb-1">Stok</label><input type="number" id={`stock-${index}`} value={variant.stock || ''} onChange={(e) => handleVariantChange(index, 'stock', parseInt(e.target.value, 10) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"/></div>
                                    <div className="w-full sm:w-36"><label htmlFor={`price_override-${index}`} className="block text-xs font-medium text-gray-600 mb-1">Özel Fiyat (₺)</label><input type="number" id={`price_override-${index}`} value={variant.price_override ?? ''} onChange={(e) => handleVariantChange(index, 'price_override', e.target.value === '' ? null : parseFloat(e.target.value))} step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Genel Fiyat"/></div>
                                    <div className="flex-1 w-full sm:w-auto"><label htmlFor={`variantImage-${index}`} className="block text-xs font-medium text-gray-600 mb-1">Varyant Görseli</label><input type="file" id={`variantImage-${index}`} accept="image/*" onChange={(e) => handleVariantImageChange(index, e)} className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-brand-green file:text-white hover:file:bg-brand-green-light cursor-pointer"/>
                                        {/* Fotoğraf Kırpma Alanı */}
                                        {editingVariantIndex === index && variant.variant_image_preview && (
                                            <div className="mt-3 bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h6 className="text-sm font-bold text-gray-900 flex items-center">
                                                        <CameraIcon className="w-4 h-4 mr-2 text-blue-600" />
                                                        Varyant Fotoğrafını Düzenle
                                                    </h6>
                                                    <div className="flex space-x-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVariantCropComplete(index)}
                                                            className="px-3 py-1 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors flex items-center"
                                                        >
                                                            <CheckCircleIcon className="w-3 h-3 mr-1" />
                                                            Uygula
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingVariantIndex(null)}
                                                            className="px-3 py-1 bg-gray-500 text-white text-xs rounded-lg hover:bg-gray-600 transition-colors"
                                                        >
                                                            İptal
                                                        </button>
                                                    </div>
                                                </div>
                                                
                                                <div className="bg-white rounded-lg p-3 shadow-inner">
                                                    <ReactCrop
                                                        crop={variantCrops[index]}
                                                        onChange={(c) => setVariantCrops(prev => ({ ...prev, [index]: c }))}
                                                        onComplete={(c) => setVariantCompletedCrops(prev => ({ ...prev, [index]: c }))}
                                                        aspect={1}
                                                        minWidth={50}
                                                        className="max-w-full"
                                                        ruleOfThirds
                                                    >
                                                        <img
                                                            src={variant.variant_image_preview}
                                                            alt="Varyant düzenleme"
                                                            onLoad={(e) => onVariantImageLoad(index, e)}
                                                            className="max-w-full h-auto rounded border border-gray-200"
                                                            style={{ maxHeight: '250px' }}
                                                        />
                                                    </ReactCrop>
                                                </div>
                                                
                                                <p className="text-xs text-blue-700 mt-2 flex items-center">
                                                    <SparklesIcon className="w-3 h-3 mr-1" />
                                                    Kare format önerilir. Fare ile sürükleyerek konumu ayarlayın.
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Fotoğraf Önizleme */}
                                        {(variant.variant_image_preview || variant.variant_image_url) && editingVariantIndex !== index && (
                                            <div className="mt-2 relative group">
                                                <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-green-400 transition-colors shadow-sm">
                                                    <img 
                                                        src={variant.variant_image_preview || variant.variant_image_url} 
                                                        alt="Varyant" 
                                                        className="w-full h-full object-cover" 
                                                    />
                                                </div>
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 flex space-x-1">
                                                        <button 
                                                            type="button" 
                                                            onClick={() => setEditingVariantIndex(index)}
                                                            className="bg-blue-500 text-white rounded-full p-1.5 shadow-lg hover:bg-blue-600 transition-colors"
                                                            title="Düzenle"
                                                        >
                                                            <AdjustmentsHorizontalIcon className="h-3 w-3" />
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleRemoveVariantImage(index)} 
                                                            className="bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600 transition-colors"
                                                            title="Sil"
                                                        >
                                                            <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Ana Ürün Görseli Kartı - Gelişmiş Fotoğraf Düzenleme */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                                <CameraIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-gray-900">Ana Ürün Görseli</h4>
                                <p className="text-xs text-gray-500">Vitrinde görünecek ana fotoğraf - Kare format önerilir</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {mainImageSrc && (
                                <div className="flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                    <CheckCircleIcon className="w-3 h-3" />
                                    <span>Hazır</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        {/* Dosya Yükleme Alanı */}
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-blue-400 transition-colors bg-gradient-to-br from-gray-50 to-blue-50">
                            <div className="text-center">
                                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                <div className="flex text-sm text-gray-600 justify-center">
                                    <label htmlFor="mainImage" className="relative cursor-pointer bg-white rounded-lg font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 px-3 py-2 border border-blue-200 hover:border-blue-300 transition-colors">
                                        <span className="flex items-center space-x-2">
                                            <CameraIcon className="w-4 h-4" />
                                            <span>Fotoğraf Seç</span>
                                        </span>
                                        <input 
                                            id="mainImage"
                                            name="mainImage" 
                                            type="file" 
                                            accept="image/*" 
                                            onChange={handleMainImageChange} 
                                            className="sr-only"
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">PNG, JPG, JPEG formatları desteklenir (Max 25MB - Kalite korunur)</p>
                            </div>
                        </div>
                        
                        {/* Fotoğraf Düzenleme Alanı */}
                        {mainImageSrc && (
                            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-blue-200">
                                <div className="flex items-center justify-between mb-4">
                                    <h5 className="text-sm font-bold text-gray-900 flex items-center">
                                        <MagnifyingGlassIcon className="w-4 h-4 mr-2 text-blue-600" />
                                        Fotoğraf Düzenleme - Vitrin Alanını Seçin
                                    </h5>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            type="button"
                                            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                                            title="Yakınlaştır"
                                        >
                                            <ArrowsPointingOutIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                                            title="Uzaklaştır"
                                        >
                                            <ArrowsPointingInIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="bg-white rounded-lg p-4 shadow-inner">
                                    <ReactCrop 
                                        crop={mainCrop} 
                                        onChange={c => setMainCrop(c)} 
                                        onComplete={c => setMainCompletedCrop(c)} 
                                        aspect={1} 
                                        minWidth={100}
                                        className="max-w-full mx-auto"
                                        ruleOfThirds
                                    >
                                        <img 
                                            ref={mainImgRef} 
                                            alt="Düzenlenecek fotoğraf" 
                                            src={mainImageSrc} 
                                            onLoad={onMainImageLoad} 
                                            className="max-w-full h-auto rounded-lg shadow-lg border border-gray-200"
                                            style={{ maxHeight: '500px', width: 'auto' }}
                                        />
                                    </ReactCrop>
                                </div>
                                
                                <div className="mt-4 bg-blue-50 rounded-lg p-3">
                                    <div className="flex items-start space-x-2">
                                        <SparklesIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-xs text-blue-800">
                                            <p className="font-medium mb-1">Düzenleme İpuçları:</p>
                                            <ul className="space-y-1 text-blue-700">
                                                <li>• Fare ile sürükleyerek kırpma alanını hareket ettirin</li>
                                                <li>• Köşelerden tutarak boyutlandırın</li>
                                                <li>• Kare format vitrinde en iyi görünümü sağlar</li>
                                                <li>• Ürün merkezi konumda olmalı</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Mevcut Görsel Önizleme */}
                        {existingMainImageUrl && !mainImageSrc && (
                            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                    <h5 className="text-sm font-bold text-gray-700">Mevcut Ana Görsel</h5>
                                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">Aktif</span>
                                </div>
                                <div className="flex justify-center">
                                    <div className="relative group">
                                        <img 
                                            src={existingMainImageUrl} 
                                            alt="Mevcut ana görsel" 
                                            className="h-40 w-40 object-cover rounded-xl shadow-lg border-2 border-gray-200 group-hover:border-brand-green transition-colors"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-xl transition-all duration-200"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Galeri Görselleri Kartı */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                                <PhotoIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-gray-900">Galeri Görselleri</h4>
                                <p className="text-xs text-gray-500">Ürün detayında görünecek ek fotoğraflar</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {(existingGalleryImageUrls.length + galleryFiles.length) > 0 && (
                                <div className="flex items-center space-x-1 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                                    <PhotoIcon className="w-3 h-3" />
                                    <span>{existingGalleryImageUrls.length + galleryFiles.length} Fotoğraf</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        {/* Dosya Yükleme Alanı */}
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-purple-400 transition-colors bg-gradient-to-br from-gray-50 to-purple-50">
                            <div className="text-center">
                                <div className="flex justify-center mb-4">
                                    <div className="flex space-x-2">
                                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                                        <PhotoIcon className="h-8 w-8 text-gray-300" />
                                        <PhotoIcon className="h-8 w-8 text-gray-200" />
                                    </div>
                                </div>
                                <div className="flex text-sm text-gray-600 justify-center">
                                    <label htmlFor="galleryImages" className="relative cursor-pointer bg-white rounded-lg font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500 px-4 py-2 border border-purple-200 hover:border-purple-300 transition-colors">
                                        <span className="flex items-center space-x-2">
                                            <PlusIcon className="w-4 h-4" />
                                            <span>Galeri Fotoğrafları Ekle</span>
                                        </span>
                                        <input 
                                            id="galleryImages"
                                            name="galleryImages" 
                                            type="file" 
                                            accept="image/*" 
                                            multiple 
                                            onChange={handleGalleryFilesChange} 
                                            className="sr-only"
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Birden fazla fotoğraf seçebilirsiniz (PNG, JPG, JPEG)</p>
                            </div>
                        </div>
                        
                        {/* Galeri Önizleme */}
                        {(existingGalleryImageUrls.length > 0 || galleryFiles.length > 0) && (
                            <div className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl p-6 border border-purple-200">
                                <h5 className="text-sm font-bold text-gray-900 mb-4 flex items-center">
                                    <PhotoIcon className="w-4 h-4 mr-2 text-purple-600" />
                                    Galeri Fotoğrafları ({existingGalleryImageUrls.length + galleryFiles.length} adet)
                                </h5>
                                
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {/* Mevcut Galeri Fotoğrafları */}
                                    {existingGalleryImageUrls.map((url, i) => (
                                        <div key={`ex-gal-${i}`} className="relative group">
                                            <div className="aspect-square rounded-lg overflow-hidden bg-white border-2 border-gray-200 group-hover:border-purple-400 transition-colors">
                                                <img 
                                                    src={url} 
                                                    alt={`Galeri ${i + 1}`} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                                />
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={() => removeExistingGalleryImage(url)} 
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 transform hover:scale-110"
                                                title="Fotoğrafı Sil"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                            <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded">
                                                Mevcut
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Yeni Galeri Fotoğrafları */}
                                    {galleryFiles.map((file, i) => (
                                        <div key={`new-gal-${i}`} className="relative group">
                                            <div className="aspect-square rounded-lg overflow-hidden bg-white border-2 border-green-200 group-hover:border-green-400 transition-colors">
                                                <img 
                                                    src={URL.createObjectURL(file)} 
                                                    alt={`Yeni ${i + 1}`} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                                />
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={() => removeNewGalleryFile(file)} 
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 transform hover:scale-110"
                                                title="Fotoğrafı Sil"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                            <div className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-1 py-0.5 rounded">
                                                Yeni
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {(existingGalleryImageUrls.length + galleryFiles.length) > 0 && (
                                    <div className="mt-4 bg-purple-50 rounded-lg p-3">
                                        <div className="flex items-start space-x-2">
                                            <SparklesIcon className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-purple-800">
                                                <p className="font-medium mb-1">Galeri İpuçları:</p>
                                                <ul className="space-y-1 text-purple-700">
                                                    <li>• Farklı açılardan çekilmiş fotoğraflar ekleyin</li>
                                                    <li>• Ürün detaylarını gösteren yakın çekimler kullanın</li>
                                                    <li>• Fotoğrafları sürükleyerek sıralayabilirsiniz</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {/* Modern Sticky Form Butonları */}
                <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-6 py-4 -mx-6 -mb-8 mt-8 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span>Değişiklikler otomatik kaydedilecek</span>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                            <button 
                                type="button" 
                                onClick={closeModal} 
                                disabled={loading} 
                                className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <XMarkIcon className="w-4 h-4 mr-2" />
                                İptal Et
                            </button>
                            
                            <button 
                                type="submit" 
                                disabled={loading} 
                                className={`w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 border-2 border-transparent rounded-xl text-sm font-bold text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-green transition-all duration-200 transform hover:scale-105 ${
                                    loading 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green shadow-brand-green/25'
                                }`}
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Kaydediliyor...</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                                        <span>{product ? 'Ürünü Güncelle' : 'Ürünü Kaydet'}</span>
                                        <SparklesIcon className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ProductForm;